import { useEffect, useRef } from 'react'
import flatpickr from 'flatpickr'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import type { Instance } from 'flatpickr/dist/types/instance'
import classNames from 'classnames'
import { fimDoDia } from '@/utility/Utils'
import styles from './style.module.css'
import './flatpickr.css'

export const MAX_RANGE_DIAS = 365

export interface DateRangePickerProps {
  id?: string
  value: Date[]
  onChange: (dates: Date[]) => void
  onRangeError?: (message: string) => void
  placeholder?: string
  invalid?: boolean
  disabled?: boolean
  maxRangeDias?: number
  maxDate?: 'today' | Date
}

const DateRangePicker = ({
  disabled = false,
  id,
  invalid = false,
  maxDate,
  maxRangeDias = MAX_RANGE_DIAS,
  onChange,
  onRangeError,
  placeholder = 'Selecione o período',
  value,
}: DateRangePickerProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const instanceRef = useRef<Instance | null>(null)
  const onChangeRef = useRef(onChange)
  const onRangeErrorRef = useRef(onRangeError)
  const maxRangeRef = useRef(maxRangeDias)
  const maxDateRef = useRef(maxDate)

  onChangeRef.current = onChange
  onRangeErrorRef.current = onRangeError
  maxRangeRef.current = maxRangeDias

  useEffect(() => {
    if (!inputRef.current) return

    const instance = flatpickr(inputRef.current, {
      mode: 'range',
      dateFormat: 'd/m/Y',
      locale: { ...Portuguese, rangeSeparator: ' até ' },
      allowInput: false,
      disableMobile: true,
      maxDate: maxDateRef.current,
      onChange: (dates) => {
        if (dates.length === 0) {
          onChangeRef.current([])
          return
        }

        if (dates.length < 2) return

        const dias = (dates[1].getTime() - dates[0].getTime()) / 86_400_000
        if (dias > maxRangeRef.current) {
          instance.clear()
          onChangeRef.current([])
          onRangeErrorRef.current?.(
            maxRangeRef.current === MAX_RANGE_DIAS
              ? 'O intervalo não pode ser superior a 1(um) ano'
              : `O intervalo não pode ser superior a ${maxRangeRef.current} dias`,
          )
          return
        }

        onChangeRef.current([dates[0], fimDoDia(dates[1])])
      },
    })

    instanceRef.current = instance

    return () => {
      instance.destroy()
      instanceRef.current = null
    }
  }, [])

  useEffect(() => {
    const instance = instanceRef.current
    if (!instance) return

    const atual = instance.selectedDates
    const iguais =
      atual.length === value.length &&
      atual.every((data, index) => data.getTime() === value[index]?.getTime())

    if (!iguais) instance.setDate(value, false)
  }, [value])

  return (
    <input
      ref={inputRef}
      id={id}
      className={classNames(styles.input, { [styles.invalid]: invalid })}
      placeholder={placeholder}
      disabled={disabled}
      readOnly
    />
  )
}

export default DateRangePicker
